import React, { useEffect, useState } from "react";
import { api } from "@/lib/auth";
import { toast } from "sonner";
import { GripVertical, Eye, EyeOff, Save, RotateCcw, Layers } from "lucide-react";

// Human-friendly labels & descriptions for known section keys
const SECTION_META = {
  hero: { label: "Hero", desc: "Full-screen headline + Book a Class / View Timetable buttons" },
  stats: { label: "Stats strip", desc: "\"5+ years · 500+ students · 60+ podiums\" numbers band" },
  events: { label: "Events", desc: "Upcoming events cards" },
  batches: { label: "Age-group batches", desc: "Kids / Teens / Adults class tiles" },
  competition: { label: "Competition crews", desc: "Juniors & Teens / Adults crew tiles with an Express-interest button" },
  why: { label: "Why parents choose us", desc: "6 selling-point cards (editable in Settings → why_features)" },
  achievements: { label: "Recent accolades", desc: "Top-6 competitions table with a link to /achievements" },
  workshops: { label: "Upcoming workshops", desc: "Workshop cards with Register CTA" },
  reviews: { label: "Google reviews", desc: "Live Google reviews (auto-hidden until Google Business is connected)" },
  hire: { label: "Hire us", desc: "Hire option cards + \"Hire us →\" CTA to /hire" },
  gallery: { label: "Gallery preview", desc: "Photo grid with a link to /gallery" },
  reels: { label: "Instagram reels", desc: "Embedded reels grid" },
  cta: { label: "Final CTA", desc: "\"Book your free trial\" full-width closing panel" },
};

export default function SectionOrder() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/settings");
      const list = (r.data?.homepage_sections || []).filter(s => s.key !== "promotions").slice().sort((a, b) => (a.order || 0) - (b.order || 0));
      setSections(list);
      setDirty(false);
    } catch { toast.error("Could not load sections"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onDragStart = (i) => (e) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (i) => (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) return;
    setSections(prev => {
      const next = prev.slice();
      const [moved] = next.splice(dragIdx, 1);
      next.splice(i, 0, moved);
      return next;
    });
    setDragIdx(i);
    setDirty(true);
  };
  const onDragEnd = () => setDragIdx(null);

  const toggleVisible = (i) => {
    setSections(prev => prev.map((s, idx) => idx === i ? { ...s, visible: !s.visible } : s));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Renumber order 1..N based on current position
      const payload = sections.map((s, i) => ({ ...s, order: i + 1 }));
      await api.put("/admin/settings", { homepage_sections: payload });
      toast.success("Section order saved — homepage updated");
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="eyebrow mb-2 flex items-center gap-2"><Layers size={12}/>Homepage layout</div>
          <h1 className="font-display text-4xl uppercase">Section order</h1>
          <p className="text-zinc-500 text-sm mt-2">Drag rows to reorder how sections appear on your homepage. Toggle the eye to hide a section without deleting it.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={saving} className="btn-ghost !py-2 !px-3 !text-xs flex items-center gap-1" data-testid="section-order-reload"><RotateCcw size={12}/>Reset</button>
          <button onClick={save} disabled={!dirty || saving} className="btn-red !py-2 !px-3 !text-xs disabled:opacity-40 flex items-center gap-1" data-testid="section-order-save"><Save size={12}/>{saving ? "Saving…" : "Save order"}</button>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 py-20 text-center">Loading…</div>
      ) : (
        <div className="card-surface" data-testid="section-order-list">
          {sections.map((s, i) => {
            const meta = SECTION_META[s.key] || { label: s.label || s.key, desc: "" };
            return (
              <div
                key={s.key}
                draggable
                onDragStart={onDragStart(i)}
                onDragOver={onDragOver(i)}
                onDragEnd={onDragEnd}
                data-testid={`section-row-${s.key}`}
                className={`flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-b-0 cursor-grab active:cursor-grabbing group ${dragIdx === i ? "bg-white/[0.03] opacity-60" : "hover:bg-white/[0.02]"} ${!s.visible ? "opacity-50" : ""}`}
              >
                <GripVertical size={16} className="text-zinc-600 group-hover:text-zinc-400 shrink-0" />
                <div className="w-6 text-zinc-500 text-xs tabular-nums">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg uppercase leading-tight text-white">{meta.label}</div>
                  <div className="text-zinc-500 text-xs truncate">{meta.desc}</div>
                </div>
                <button
                  onClick={() => toggleVisible(i)}
                  className={`p-2 border ${s.visible ? "border-emerald-500/40 text-emerald-400" : "border-zinc-700 text-zinc-500"} hover:border-red-500 hover:text-red-500`}
                  title={s.visible ? "Visible — click to hide" : "Hidden — click to show"}
                  data-testid={`section-toggle-${s.key}`}
                >
                  {s.visible ? <Eye size={14}/> : <EyeOff size={14}/>}
                </button>
              </div>
            );
          })}
          {sections.length === 0 && <div className="text-zinc-500 text-center py-16">No sections configured.</div>}
        </div>
      )}
      {dirty && (
        <div className="text-amber-500 text-xs mt-3" data-testid="section-order-dirty">Unsaved changes. Click "Save order" to apply.</div>
      )}
    </div>
  );
}
